import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { KPICard, WidgetCard, Pill } from '@/components/ui/native';
import { CheckCircle2 } from 'lucide-react';

function SecurityDashboard() {
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    event_type: '',
    action: '',
    success: '',
    days: 7
  });
  const [pagination, setPagination] = useState({
    skip: 0,
    limit: 20,
    total: 0
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchStats();
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.days]);

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.skip, filters.event_type, filters.action, filters.success]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/auth/audit-logs/stats?days=${filters.days}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load security stats');
    }
  };

  const fetchLogs = async () => {
    setLogsLoading(true);
    try {
      const params = new URLSearchParams({
        skip: pagination.skip.toString(),
        limit: pagination.limit.toString()
      });
      
      if (filters.event_type) params.append('event_type', filters.event_type);
      if (filters.action) params.append('action', filters.action);
      if (filters.success !== '') params.append('success', filters.success);
      
      const response = await axios.get(`${API}/auth/audit-logs?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(response.data.logs);
      setPagination(prev => ({ ...prev, total: response.data.total }));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load audit logs');
    } finally {
      setLoading(false);
      setLogsLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="security-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Security Dashboard</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">VAPT & SOC2 Compliance Monitoring</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={filters.days}
            onChange={(e) => setFilters(prev => ({ ...prev, days: parseInt(e.target.value) }))}
            className="px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 dark:bg-slate-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400"
            data-testid="period-filter"
          >
            <option value={1}>Last 24h</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Security Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard title="Successful Logins" value={stats.successful_logins} testId="successful-logins-card" />
          <KPICard title="Failed Auth Attempts" value={stats.failed_auth_attempts} testId="failed-auth-card" />
          <KPICard title="New Registrations" value={stats.new_registrations} testId="new-registrations-card" />
          <KPICard title="Suspicious IPs" value={stats.unique_failed_ips} testId="suspicious-ips-card" />
        </div>
      )}

      {/* Security Configuration */}
      <WidgetCard title="Security Configuration" headerRight={null}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            { label: 'Rate Limiting',     hint: '100 req/60s per IP' },
            { label: 'Login Protection',  hint: '5 attempts, 5min lockout' },
            { label: 'Security Headers',  hint: 'HSTS, CSP, X-Frame' },
            { label: 'Audit Logging',     hint: 'All auth events tracked' },
            { label: 'Password Policy',   hint: '8+ chars, mixed case, special' },
            { label: 'Input Validation',  hint: 'XSS & NoSQL injection protection' },
          ].map((c) => (
            <div key={c.label} className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700/40 border border-slate-200 dark:border-slate-700 rounded-lg">
              <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{c.label}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{c.hint}</p>
              </div>
            </div>
          ))}
        </div>
      </WidgetCard>

      {/* Audit Logs Table */}
      <WidgetCard
        title="Audit Logs"
        headerRight={
          <div className="flex items-center gap-2">
            <select
              value={filters.action}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, action: e.target.value }));
                setPagination(prev => ({ ...prev, skip: 0 }));
              }}
              className="px-2.5 py-1 text-xs border border-slate-200 dark:border-slate-700 dark:bg-slate-900 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-400"
              data-testid="action-filter"
            >
              <option value="">All Actions</option>
              <option value="login_success">Login Success</option>
              <option value="login_failed">Login Failed</option>
              <option value="register">Register</option>
              <option value="sso_login_success">SSO Login</option>
              <option value="sso_register_success">SSO Register</option>
              <option value="sso_domain_rejected">Domain Rejected</option>
            </select>
            <select
              value={filters.success}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, success: e.target.value }));
                setPagination(prev => ({ ...prev, skip: 0 }));
              }}
              className="px-2.5 py-1 text-xs border border-slate-200 dark:border-slate-700 dark:bg-slate-900 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-400"
              data-testid="success-filter"
            >
              <option value="">All Results</option>
              <option value="true">Success</option>
              <option value="false">Failed</option>
            </select>
          </div>
        }
        className="overflow-hidden"
      >
        <div className="overflow-x-auto -mx-4 -my-4">
          <table className="table-modern w-full">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Action</th>
                <th>User/Email</th>
                <th>IP Address</th>
                <th>Status</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {logsLoading ? (
                <tr>
                  <td colSpan={6} className="text-center py-8">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    </div>
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-slate-500 dark:text-slate-400">
                    No audit logs found for the selected filters
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id || log.timestamp}>
                    <td className="whitespace-nowrap">
                      <span className="text-sm text-slate-600 dark:text-slate-400">{formatTimestamp(log.timestamp)}</span>
                    </td>
                    <td>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        log.action.includes('success') ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                        log.action.includes('failed') || log.action.includes('rejected') ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' :
                        'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                      }`}>
                        {log.action.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="text-sm text-slate-900 dark:text-white">
                      {log.details?.email || log.user_id || '-'}
                    </td>
                    <td className="text-sm text-slate-600 dark:text-slate-400 font-mono">
                      {log.ip_address || '-'}
                    </td>
                    <td>
                      {log.success ? (
                        <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span className="text-xs font-medium">Success</span>
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          <span className="text-xs font-medium">Failed</span>
                        </span>
                      )}
                    </td>
                    <td className="text-xs text-slate-500 dark:text-slate-400">
                      {log.details?.provider && (
                        <span className="px-2 py-1 bg-slate-100 dark:bg-slate-700 rounded text-slate-600 dark:text-slate-300 mr-1">
                          {log.details.provider}
                        </span>
                      )}
                      {log.details?.reason && (
                        <span className="text-red-500 dark:text-red-400">{log.details.reason}</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.total > pagination.limit && (
          <div className="px-4 py-3 -mx-4 -mb-4 mt-4 border-t border-slate-100 dark:border-slate-700 flex items-center justify-between">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Showing {pagination.skip + 1} to {Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total} entries
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, skip: Math.max(0, prev.skip - prev.limit) }))}
                disabled={pagination.skip === 0}
                className="px-3 py-1 text-xs border border-slate-200 dark:border-slate-700 rounded-md hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="prev-page-btn"
              >
                Previous
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip + prev.limit }))}
                disabled={pagination.skip + pagination.limit >= pagination.total}
                className="px-3 py-1 text-xs border border-slate-200 dark:border-slate-700 rounded-md hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="next-page-btn"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </WidgetCard>
    </div>
  );
}

export default SecurityDashboard;
